import { Combobox, DatePicker, Errors, Field, FormValue, RadioboxGroup, SimpleRadiobox, TextInput, WithForm, validation } from '@startlibs/form';
import {Dialog, Button, SplitColumnsContainer, ConfirmDialog, Loading, Icon, SwitchDiv} from '@startlibs/components'
import {addQueryString, callIfFunction, formFetch, formatDate, getColor, getFetcher, media, postFetcher, smoothScroll} from '@startlibs/utils'
import React, { useEffect, useState } from 'react'
import _ from 'lodash/fp'

import {useNotificationRef} from '../../components/Notifications'
import styled, { css } from 'styled-components';
import { Fill, Slot, useConstant, useRefState, useToggle } from '@startlibs/core';
import { CurrentPatientStudyCard } from './CurrentPatientStudyCard';
import { callUpdateParamsPat, useRegisterSetParamsPat, useUpdateParamsPat } from '../../hooks/useUpdateParamsPat';
import { cachedPatientFetcher } from '../utils/cachedPatientFetcher';
import { Pagination } from '../../components/Pagination';
import { FetchErrorDialog } from '../errors/FetchErrorDialog';
import { dicomDateToDate, formatNoUTCDate } from '../../utils';
import { CardHeader } from '../errors/ProviderError';

const ErrorCard = styled.div`
  padding: 2.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  max-width: 40rem;
  margin: 0 auto;
  p {
    font-size: 14px;
  }
`

const FlexDiv = styled.div`
  display: flex;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`

const LoadingDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`

const SearchInput = styled.div`
  position: relative;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 100;
  > input {
    word-break: normal;
    padding-right: 2.75rem;
    padding-left: 2.75rem;
    border-radius: 0px;
    border: none;
    border-bottom: 1px solid rgba(0,0,0,0.18);
    :focus {
      box-shadow: none;
      border-bottom: 1px solid rgba(0,0,0,0.18);
    }
  }
`

const ClearSearchButton = styled(Icon)`
  color: ${getColor('gray90')};
  font-size: 12px;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  width: 1.5rem;
  line-height: 1.5rem;
  text-align: center;
  border-radius: 50%;
  background: rgba(0,0,0,0.1);
  :hover {
    background: #f7d3d4;
    color: ${getColor('alert')};
  }
`

const SearchIcon = styled(Icon)`
  position: absolute;
  z-index: 100;
  left: 8px;
  bottom: 7px;
  font-size: 22px;
  color: ${getColor('gray150')};
`

const EmptyList = styled.div `
  background: ${getColor('gray240')};
  color: ${getColor('gray120')};
  height: 100%;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 14px;
  flex-grow: 1;
`

const PatientList = styled.div`
  margin-top: 1.5rem;
  border: 1px solid ${getColor('gray210')};
  border-radius: 5px;
  overflow: hidden;
`

const StyledPatientRow = styled.div`
  cursor: pointer;
  &:nth-child(even) {
    background-color: #EFF4F7;
  }
  &:hover {
    background: rgba(0,139,210,0.15);
  }
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${getColor('gray210')};
  padding: 1rem;
  &:last-child {
    border-bottom: 0px;
  }
  .info-box {
    width: 260px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-content: center;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.15rem;
  }
  ${props => props.selected && css`
    &:nth-child(odd), &:nth-child(even) {
      background-color: ${getColor('main')};
      color: white;
      &:hover {
        background: ${getColor('main')};
      }
    }
    ${Icon} {
      position: absolute;
      left: 50%;
      right: 50%;
      transform: translate(-50%,-50%);
      color: ${getColor('main')};
      width: 10px;
      height: 11px;
      font-weight: 600;
    }
  `}
`

const PatientRadioBox = styled(SimpleRadiobox)`
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  > div > div {
    position: static;
    > div {
      left: 50%;
      transform: translate(-50%,-50%);
      ${props => props.selected && css`
        background: white;
        border: transparent;
        &:before {
          background: transparent;
        }
      `}
    }
    > span {
      display: none;
    }
  }
`

const ListArea = styled.div`
  border: 1px solid ${getColor('gray210')};
  border-radius: 6px;
  min-height: 200px;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  margin-bottom: 1rem;
  .table-footer {
    padding: 0.5rem;
    display: flex;
    flex-wrap: nowrap;
    align-content: center;
    justify-content: center;
    align-items: center;
    background: white;
    width: 100%;
    box-shadow: 0 -1px 0 0 rgb(210,210,210);
    border-radius: 0px 0px 5px 5px;
    position: sticky;
    bottom: -1px;
    margin-top: auto;
  }
`

const TableLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  h4 {
    margin-bottom: 0.5rem;
    font-size: 14px;
  }
`
const PatientsFound = styled.b`
`

export const DEST_NEW = "New"
export const DEST_EXISTING = "Existing"
const MSG_REQUIRED = "Field is required"
const MSG_NAME_LENGTH = "Name must be less than 60 characters."
const MSG_NAME_LENGTH_2 = "name must be at least 2 characters long."
const MSG_PATIENT_ID_LENGTH = "Patient ID must be shorter than 64 characters"

const preValidation = validation.buildValidation({
  "patientID": [(v,k,values) => ((values.destination ===DEST_NEW) && (!v || !v.length) && validation.required()) 
    || ((values.destination === DEST_NEW) && v?.length > 64 && MSG_PATIENT_ID_LENGTH)
  ],
  "firstName": [(v,k,values) =>((values.destination === DEST_NEW) && (values?.firstName?.length + values?.middleName?.length + values?.lastName?.length > 60) && MSG_NAME_LENGTH)
    || ((values.destination === DEST_NEW) && (values?.firstName?.length + values?.middleName?.length + values?.lastName?.length < 1) && 'At least one name is required.')
  ],
  "middleName": [(v,k,values) => ((values?.firstName?.length + values?.middleName?.length + values?.lastName?.length > 60) && MSG_NAME_LENGTH)
    || ((values.destination === DEST_NEW) && (values?.firstName?.length + values?.middleName?.length + values?.lastName?.length < 1) && 'At least one name is required.')
  ],
  "lastName": [(v,k,values) => ((values.destination === DEST_NEW) && (values?.firstName?.length + values?.middleName?.length + values?.lastName?.length > 60) && MSG_NAME_LENGTH)
    || ((values.destination === DEST_NEW) && (values?.firstName?.length + values?.middleName?.length + values?.lastName?.length < 1) && 'At least one name is required.')
  ],
  "patientSex": [(v,k,values) => ((values.destination === DEST_NEW) && (values?.patientSex !="M" && values?.patientSex !="F") && MSG_REQUIRED)],
  "selectedPatient": [(v,k,values) => ((values.destination === DEST_EXISTING) && (!v && "Please select the destination patient"))],
  "patientDOB": [(v,k,values) => ((values.destination === DEST_NEW) && v == -1 && 'Invalid date')],
})

const DATE_FORMAT = "yyyy-MM-dd"
const TRANSFORM_DATE_FORMAT = "yyyyMMdd"

const name1 = "do Nascimento^Gabriel^Monteiro";
// const name1 = "Adams^John Robert Quincy^^Rev.^B.A. M.Div.";
// 0: "Adams" // Family Name
// 1: "John Robert Quincy" // Given Name
// 2: "" // Middle Name
// 3: "Rev." // Prefix
// 4: "B.A. M.Div." // Suffix

export const buildName = (nameObject) => {
  const nameArray = [
    nameObject?.lastName,
    nameObject?.firstName,
    nameObject?.middleName,
    nameObject?.prefix,
    nameObject?.suffix,
  ]
  const nameString = nameArray.join("^")
  return nameString
}

const splitName = (name) => {
  const nameArray = name.split("^")
  const nameObject = {
    lastName: nameArray[0],
    firstName: nameArray[1],
    middleName: nameArray[2],
    prefix: nameArray[3],
    suffix: nameArray[4],
  }
  return nameObject
}

const transform = (values, patientData) => {
  
  // return {
  //   form: values,
  //   updatedTags: [
  //     values.patientID !== patientData.patientID && {"tagID" : "0010,0020", "tagValue" : values.patientID},
  //     name !== patientData.patientName && {"tagID" : "0010,0010", "tagValue" : name},
  //     dob !== patientData.patientDOB && {"tagID" : "0010,0030", "tagValue" : dob},
  //     values.patientSex !== patientData.patientSex && {"tagID" : "0010,0040", "tagValue" : values.patientSex},
  //   ].filter(Boolean)
  // }
}

const postDataUpdateAndClose = (jsonValues, study, setStudy, closeDialog, setNotification, setIsSubmitting) => {
  // Make the post to the backend
  postFetcher("/changepatient.php", jsonValues)
    .then((response) => {
      // set Study method will update this studys row in the worklist with the updated information
      setStudy((study) => {
        return {
          ...study,
          patient: {
            ...study.patient,
            patientID: jsonValues.logContent.newPatientID,
            patientName: jsonValues.logContent.newPatientName.replace(/\^/g, ' '),
            patientDOB: jsonValues.logContent.newPatientDOB,
          },
        }
      })
      setIsSubmitting(false)
      setNotification("Patient changed successfully.")
      closeDialog()
    })
    .catch((error) => {
      console.log("Failed to change patient")
      setNotification({type:"alert",msg:<>Failed to change patient.</>})
      setIsSubmitting(false)
    })
}

const createPostData = (form, patientData, studyUid, study, setStudy, closeDialog, setNotification, setIsSubmitting, confirmDialog, requireConfirmation) => {
  setIsSubmitting(true)

  const values = form.getValues()
  const name = buildName(values)
  const dob = formatDate(values.patientDOB,TRANSFORM_DATE_FORMAT)
  const newPatiendID = values.destination === DEST_NEW ? values.patientID : values.selectedPatient.patient.patientID
  const jsonValues =  values.destination === DEST_NEW 
    ? {
        study_iuid :  studyUid,
        study_desc: patientData.study_desc,
        systemData: {
          studyIuid: studyUid,
          patientId: newPatiendID,
          patientName: name,
          patientBirthDate: dob,
          patientSex: values.patientSex,
        },
        logContent:{
          dest: "N",
          oldPatientName: patientData.patientName,
          oldPatientID: patientData.patientID,
          oldPatientDOB: patientData.patientDOB,
          oldPatientSex: patientData.patientSex,
          newPatientName: name,
          newPatientID: values.patientID,
          newPatientDOB: dob,
          newPatientSex: values.patientSex,
          oldPatientPK: patientData.patientPK,
        }
      } 
    : {
        study_iuid :  studyUid,
        study_desc: patientData.study_desc,
        systemData: {
          studyIuid: studyUid,
          patientPk:  Number(values.selectedPatient.patient.patientPK),
        },
        logContent:{
          dest: "E",
          oldPatientName: patientData.patientName,
          oldPatientID: patientData.patientID,
          oldPatientDOB: patientData.patientDOB,
          newPatientName: values.selectedPatient.patient.patientName,
          newPatientID: values.selectedPatient.patient.patientID,
          newPatientDOB: values.selectedPatient.patient.patientDOB,
          oldPatientPK: patientData.patientPK,
          newPatientPK: Number(values.selectedPatient.patient.patientPK),
        }
      }

  if(requireConfirmation && values.destination === DEST_NEW){
  
    getFetcher(addQueryString('/list_patients.php?'), { 
      page: 1,
      maxResults: 10,
      patientId: newPatiendID,
    })
    .then((response) => {
      setIsSubmitting(false)
      if(response.patients.dbCount > 0){
        // alert the user that the patient already exists
        confirmDialog.toggle()
      }else{
        postDataUpdateAndClose(jsonValues, study, setStudy, closeDialog, setNotification, setIsSubmitting)    
      }
    })
  }else{
    postDataUpdateAndClose(jsonValues, study, setStudy, closeDialog, setNotification, setIsSubmitting)
  }
}

export const ChangePatientDialog = ({study, setStudy, closeDialog}) => {
  const [,setNotification] = useNotificationRef()

  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [enableSubmit, setEnableSubmit] = useState(false)
  const [patientData, setPatientData] = useState({})

  const studyUid = study.tools.dicomEditWeblink.split("study_iuid=")[1]

  const loading = useToggle()

  const [paramsPat, rawSetParamsPat] = useState({search: ''})

  const updateParamsPat = (paramsPat) => {
    callUpdateParamsPat(paramsPat)
  }

  const debounceSetParamsPat = useConstant(_.debounce(500, rawSetParamsPat))

  const setParamsPat = (updater, debounce) => {
    const verifyPage = (updater) => (state) => {
      const updatedState = callIfFunction(updater, state)
      if (
        state.search !== updatedState.search
      ) {
        return _.flow(
          _.set('page', 1),
          _.unset('forceAll')
        )(updatedState)
      } else if (state.maxResults !== updatedState.maxResults) {
        const firstRow = ((state.maxResults || 10) * (updatedState.page - 1) + 1)
        return _.set('page', Math.max(Math.floor(firstRow / (updatedState.maxResults || 10)) + 1, 1), updatedState)
      } else {
        return updatedState
      }
    }
    if (debounce) {
      debounceSetParamsPat(verifyPage(updater))
    } else {
      setTimeout(() => rawSetParamsPat(verifyPage(updater)), 0)
    }
    setTimeout(loading.open, 0)
  }
  useRegisterSetParamsPat(setParamsPat)

  useEffect(() => {
  
    const getDicomTagsUrl = study.tools.dicomEditWeblink
    getFetcher(getDicomTagsUrl)
      .then((response) => {
        const nameObject = splitName(response.patientName)
        const newPatientData = {
          patientID: response.patientID,
          firstName: nameObject.firstName,
          middleName: nameObject.middleName,
          lastName: nameObject.lastName,
          patientName: response.patientName,
          patientSex: response.patientSex,
          patientDOB: response.patientDOB,
          institutionName: response.institutionName,
          study_desc: response.study_desc,
          countStudies: response.countStudies,
          patientPK: response.patientPK
        }
        setPatientData(newPatientData)
        setIsLoading(false)  
      })
      .catch((error) => {
        setLoadError(true)
        setIsLoading(false)
      });

  },[])

  const query = useToggle(paramsPat.search || '')
  const confirmDialog = useToggle()

  useUpdateParamsPat((paramsPat) => {
    query.openWith(paramsPat.search || "")
  })

  const updateSearch = (v) => {
    query.openWith(v)
    setParamsPat(_.set('search', v), true)
  }

  const [patients, setPatients] = useState(null)
  const [selectedPatient, setSelectedPatient] = useState()
  const lastParams = useRefState(paramsPat)
  const lastSuccessParams = useRefState(paramsPat)
  const errorDialog = useToggle()

  const maxResults = Number(paramsPat.maxResults) || 10
  const currentPage = Number(paramsPat.page) || 1

  const loadList = useConstant((currentParams, closeLoading, refreshing) => {
    lastParams.set(currentParams)
    if (lastParams.get() === lastSuccessParams.get() && !refreshing) {
      callIfFunction(closeLoading)
      return Promise.resolve()
    }
    if((currentParams.search||"").length < 1){
      lastSuccessParams.set(currentParams)
      window.scroll(0, window.pageYOffset);
      smoothScroll(0)
      setPatients({patients:{blankList:true, dbCount:0,rows:[]}, key: Date.now()})
      callIfFunction(closeLoading)
      return Promise.resolve()
    }
    return cachedPatientFetcher({
      page: currentParams.page,
      maxResults: 10,
      search: currentParams.search,
    }, refreshing)
      .then(({patients}) => {
          if (lastParams.get() === currentParams) {
            lastSuccessParams.set(currentParams)
            window.scroll(0, window.pageYOffset);
            smoothScroll(0)
            setPatients({patients, key: Date.now()})
            callIfFunction(closeLoading)
          }
        }
      )
      .catch(() => {
        if (lastParams.get() === currentParams) {
          callIfFunction(closeLoading)
          errorDialog.open()
          updateParamsPat(lastSuccessParams.get())
        }
      })
  })

  useEffect(() => {
    loading.open()
    loadList(paramsPat, loading.close)
  }, [paramsPat])

  
  return <WithForm
    action={(values) => {
    }}
    // onSuccess={(values) => {
    // }}
    values={{destination: DEST_EXISTING, firstName: '', middleName: '', lastName: ''}}
    // transform={(v) => transform(v, patientData)}
    preValidation={preValidation}
  >{form =>
    <Dialog
      title="Change associated patient"
      closeDialog={closeDialog}
      footer={ () => {
        return <>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button type="submit" highlight 
            disabled={!form.hasChanged && !enableSubmit}
            onClick={
              () => {
                form.willSubmitForm().then(() => {
                  setEnableSubmit(true)
                  createPostData(form, patientData, studyUid, study, setStudy, closeDialog, setNotification, setIsSubmitting, confirmDialog, true)
                })
              }
            }
            isLoading={isSubmitting}>
            Save
          </Button>
        </>}
      }
    >
      {isLoading 
        ? <LoadingDiv css="height:200px;">
            <Loading size={30} borderWidth={5}/>
          </LoadingDiv> 
        : loadError 
          ? <ErrorCard>
              <CardHeader>
                <h1>Something went wrong</h1>
              </CardHeader>
              <p><b>Please reload the page and try again.</b></p>
              <p>If the error persists please contact <a href="mailto:support@purview.net"
                                                        target="_blank">support@purview.net</a> or <span
                className="nowrap">call <b>+1 800-501-1537</b>.</span></p>
            </ErrorCard>
          : <>
              <FormValue>{(values) => <>
              <CurrentPatientStudyCard study={study} patientName={patientData.patientName} patientID={patientData.patientID} patientDOB={patientData.patientDOB} fValues={values}/>
              <RadioboxGroup path="destination" framedBoxes horizontal wide highlightSelected>
                <SimpleRadiobox label="To an existing patient"  fieldValue="Existing"/>
                <SimpleRadiobox label="To a new patient" fieldValue="New"/>
              </RadioboxGroup>
              {/* <FormValue>{(values) => <> */}
                <SwitchDiv on={values.destination} css="margin-top: 12px;">
                  {values.destination === "Existing" && <>
                    <TableLabel>
                      <h4>Select the patient</h4>
                      {patients && patients.patients.rows?.length > 0 && <>
                        <PatientsFound>{patients.patients.dbCount} patient{patients.patients.dbCount !== 1 ? "s" : ""}{paramsPat.search && " found"}</PatientsFound>
                      </>}
                    </TableLabel>
                    <ListArea>
                      <SearchInput>
                        <SearchIcon icon="search"/>
                        <TextInput raw 
                          value={query.isOpen} 
                          setValue={updateSearch} 
                          placeholder="Patient name, ID, or DOB"
                        />
                        {query.isOpen && <ClearSearchButton icon="x" onClick={() => {
                          updateSearch("")
                          setSelectedPatient(null)
                          form.setValues(values => _.unset('selectedPatient', values))
                          }}/>}
                      </SearchInput>
                      {loading.isOpen 
                        ? <LoadingDiv>
                            <Loading size={24} borderWidth={4}/>
                          </LoadingDiv> 
                        : <>
                            {((!patients) || (!paramsPat.search)) && <>
                              <EmptyList><div>Enter a name, ID or DOB to search for the patient</div></EmptyList>
                            </>}
                            {patients && patients.patients.rows?.length > 0 && <>
                              {patients && Number(patients.patients.dbCount) > 10 && <Fill name="PaginationPat"><Pagination
                                setPageParam={(page) => () => setParamsPat(_.set('page', page))}
                                currentPage={currentPage}
                                totalPages={Math.ceil((Number(patients.patients.dbCount)) / maxResults)}
                              /></Fill>}
                              {patients.patients.rows.map((patient, i) => 
                                <FormValue path="selectedPatient">{(selectedPatient, setSelectedPatient) =>
                                  <PatientRow
                                    selected={selectedPatient === patient} setSelectedPatient={setSelectedPatient}
                                    patient={patient}
                                  />
                                }</FormValue>
                              )}
                              <div className='table-footer'><Slot name="PaginationPat"/></div>
                            </>}
                            {patients && patients.patients.rows?.length === 0 && paramsPat.search && 
                              <EmptyList>No records found</EmptyList>
                            }
                          </>
                      } 
                      {
                        errorDialog.isOpen &&
                        <FetchErrorDialog closeDialog={errorDialog.close}/>
                      }
                    </ListArea>
                  </>}
                  {values.destination === "New" && <div>
                    <TextInput 
                      label="Patient ID"
                      path="patientID"
                      value={study.patient.id}
                      // descText={"Changing this value may affect log filtering for this patient."}
                      mandatory
                    ></TextInput>
                    <FlexDiv>
                      <TextInput 
                        label="First Name"
                        // path="patientName"
                        path="firstName"
                        // mandatory
                        onInput={(e) => e.target.value = ("" + e.target.value).toUpperCase()}
                      ></TextInput>
                      <TextInput 
                        label="Middle Name"
                        path="middleName"
                        onInput={(e) => e.target.value = ("" + e.target.value).toUpperCase()}
                      ></TextInput>
                      <TextInput 
                        label="Last Name"
                        path="lastName"
                        // mandatory
                        onInput={(e) => e.target.value = ("" + e.target.value).toUpperCase()}
                      ></TextInput>
                    </FlexDiv>
                    <SplitColumnsContainer>
                      <DatePicker label="Patient date of birth" path="patientDOB" format={DATE_FORMAT} />
                      <RadioboxGroup path="patientSex" label="Gender" mandatory framedBoxes horizontal wide>
                        <SimpleRadiobox label="Male"  fieldValue="M"/>
                        <SimpleRadiobox label="Female" fieldValue="F"/>
                      </RadioboxGroup>
                    </SplitColumnsContainer>
                  </div>}
              </SwitchDiv>
              </>}</FormValue>
              <Errors/>
              {
                confirmDialog.isOpen &&
                <ConfirmDialog 
                  closeDialog={confirmDialog.close}
                  title="Error"
                  footer={<>
                    <Button onClick={confirmDialog.close}>Return</Button>
                    {/* <Button type="submit" highlight onClick={
                      () => {
                        const closeBoth = () => {
                          confirmDialog.close()
                          closeDialog()
                        }
                        createPostData(form, patientData, studyUid, study, setStudy, closeBoth, setNotification, setIsSubmitting, confirmDialog, false)
                      }
                    } isLoading={isSubmitting}>Proceed anyway</Button> */}
                  </>}
                >
                  <p><b>There already is an existing patient with this Patient ID.</b></p>
                  <p>Confirm that you entered the new patient information correctly or associate this study with the existing patient.</p>
                </ConfirmDialog>
              }
            </>
      }
    </Dialog>
  }</WithForm>
}

const PatientRow = ({selected, patient, setSelectedPatient}) => {
  const name = patient.patient.patientName.replace(/\^/g, ' ').trim()
  const dob = dicomDateToDate(patient.patient.patientDOB)
  const formattedDob = isNaN(dob.getTime()) ? "" : formatNoUTCDate(dob, DATE_FORMAT, undefined, true)

  return <StyledPatientRow selected={selected} onClick={() => selected ? setSelectedPatient(null) : setSelectedPatient(patient)}> 
    <div>
      <div><b>{name}</b></div>
      <div className='info-box'>
        <div><b>ID:</b> {patient.patient.patientID}</div>
        <div css="min-width:113px;"><b>DOB:</b> {formattedDob}</div>
      </div>
    </div>
    <div className="check" css="position:relative">
      <PatientRadioBox selected={selected} path="selectedPatient" fieldValue={patient}/>
      {selected && <Icon icon="check"/>}
    </div>
  </StyledPatientRow>
}